/**
 * AdminFeedbackPanel — reportes de problemas y sugerencias de los usuarios
 * (Envío AP-1). Se marcan como Nuevo → Visto → Resuelto, con nota interna.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Lightbulb, Loader2, MessageSquareWarning, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { learningService } from '../../services/learningService';
import './admin-learning.css';

const STATUS = { new: 'Nuevo', seen: 'Visto', done: 'Resuelto' };
const fmt = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export default function AdminFeedbackPanel() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('new');
  const [kind, setKind] = useState('');
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await learningService.adminFeedback({ status: status || undefined, kind: kind || undefined });
      setItems(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudieron cargar los mensajes');
    } finally { setLoading(false); }
  }, [status, kind]);

  useEffect(() => { load(); }, [load]);

  const update = async (item, patch) => {
    try {
      await learningService.adminUpdateFeedback(item.id, patch);
      toast.success('Actualizado');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo actualizar');
    }
  };

  return (
    <div className="sml-adl is-single" data-testid="admin-feedback-panel">
      <div className="sml-adl-main">
        <header className="sml-adl-head">
          <div>
            <h3><MessageSquareWarning size={18} aria-hidden="true" /> Reportes y sugerencias</h3>
            <p>Lo que mandan los usuarios desde ? › Reportar un problema y ? › Sugerir una mejora.</p>
          </div>
          <div className="sml-adl-head-actions">
            <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Tipo">
              <option value="">Todo</option>
              <option value="problem">Problemas</option>
              <option value="idea">Mejoras</option>
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Estado">
              <option value="">Todos los estados</option>
              <option value="new">Nuevos</option>
              <option value="seen">Vistos</option>
              <option value="done">Resueltos</option>
            </select>
            <button type="button" className="sml-adl-icon" onClick={load} aria-label="Actualizar" disabled={loading}>
              {loading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            </button>
          </div>
        </header>

        <ul className="sml-adf-list">
          {items.map((item) => (
            <li key={item.id} className="sml-adf-item">
              <div className="sml-adf-meta">
                <span className={`sml-adf-kind is-${item.kind}`}>
                  {item.kind === 'idea' ? <Lightbulb size={13} /> : <AlertTriangle size={13} />}
                  {item.kind === 'idea' ? 'Mejora' : 'Problema'}
                </span>
                <span>{item.user_email || item.user_id}</span>
                <span>{fmt(item.created_at)}</span>
                {item.context?.project_name && <span>Proyecto: {item.context.project_name}</span>}
                {item.context?.view && <span>Vista: {item.context.view}</span>}
                <span className={`sml-adl-status is-fb-${item.status}`}>{STATUS[item.status]}</span>
              </div>
              <p className="sml-adf-msg">{item.message}</p>
              {item.context?.user_agent && (
                <p className="sml-adf-ctx">{item.context.viewport} · {item.context.user_agent}</p>
              )}
              <div className="sml-adf-actions">
                <input placeholder="Nota interna (opcional)" value={notes[item.id] ?? item.admin_note ?? ''}
                       onChange={(e) => setNotes((n) => ({ ...n, [item.id]: e.target.value }))} />
                {item.status !== 'seen' && item.status !== 'done' && (
                  <button type="button" className="sml-adl-ghost"
                          onClick={() => update(item, { status: 'seen', admin_note: notes[item.id] })}>Marcar visto</button>
                )}
                {item.status !== 'done' && (
                  <button type="button" className="sml-adl-primary"
                          onClick={() => update(item, { status: 'done', admin_note: notes[item.id] })}>Resuelto</button>
                )}
                {item.status === 'done' && (
                  <button type="button" className="sml-adl-ghost"
                          onClick={() => update(item, { status: 'new' })}>Reabrir</button>
                )}
              </div>
            </li>
          ))}
          {!loading && items.length === 0 && <li className="sml-adl-empty">No hay mensajes con este filtro.</li>}
        </ul>
      </div>
    </div>
  );
}
